<template>
	<div>
		<div class="d-flex align-items-center mb-3">
			<b-breadcrumb class="flex-grow-1 mb-0">Users</b-breadcrumb>
			<b-button @click="$refs.editModal.show()" variant="success" size="sm" class="ml-2" v-b-tooltip.hover.left.v-success title="Add a user"><b-icon icon="plus"></b-icon></b-button>
		</div>
		<b-form-row>
			<b-col>
				<search-input v-model="q" placeholder="Search..." class="mb-2"></search-input>
			</b-col>
			<b-col>
				<b-form-select v-model="status" :options="[{value:'',text:'Status...'},'active','gratuity','none','incomplete_expired']"/>
			</b-col>
			<b-col>
				<b-form-select v-model="wsActive" :options="[{value:'',text:'Websocket activity...'},{value:'active',text:'Active'},{value:'inactive',text:'Inactive'}]"/>
			</b-col>
		</b-form-row>
		<b-table small striped hover :items="users" :busy="loading>0" :fields="['username','activity','billing']">
			<template #cell(username)="data">
				<b-dropdown variant="link" toggle-class="p-0 text-body text-decoration-none" size="sm">
					<template #button-content>{{data.item.username}} <code>{{data.item.id}}</code></template>
					<b-dropdown-item @click="$refs.editModal.show(data.item.id)"><b-icon icon="pencil-fill"></b-icon> Edit user</b-dropdown-item>
					<b-dropdown-item @click="$refs.deleteModal.show(data.item.id)" variant="danger"><b-icon icon="trash"></b-icon> Delete user</b-dropdown-item>
					<b-dropdown-divider></b-dropdown-divider>
					<b-dropdown-item @click="grantGratuity(data.item.id, 'regular')">Grant Regular gratuity</b-dropdown-item>
					<b-dropdown-item @click="grantGratuity(data.item.id, 'pro')">Grant Pro gratuity</b-dropdown-item>
					<b-dropdown-item @click="revokeGratuity(data.item.id)">Revoke gratuity</b-dropdown-item>
					<b-dropdown-divider></b-dropdown-divider>
					<b-dropdown-item @click="syncRestreaming(data.item.id)">Recreate ingest and egress</b-dropdown-item>
					<b-dropdown-item @click="loginAsUser(data.item.id)">Login as user</b-dropdown-item>
				</b-dropdown><br />
				<small :class="{'text-danger':!data.item.email_confirmed}">
					<a v-if="data.item.email" :href="`mailto:${data.item.email}`">{{data.item.email}}</a>
					<span v-if="!data.item.email_confirmed"> (unconfirmed)</span>
				</small><br />
				<small class="text-muted" v-if="data.item.signup_date"><time-ago :date="data.item.signup_date" /></small><br />
				<small class="text-muted" v-if="data.item.signup_ip"><code>{{data.item.signup_ip}}</code><span v-if="data.item.signup_country"> ({{data.item.signup_country}})</span></small>
			</template>
			<template #cell(activity)="data">
				<div class="channel-icons">
					<template v-for="(ch, idx) in visibleChannels(data.item)">
						<a
							:key="`link-${ch.service_id}-${ch.channel_token}`"
							:id="channelIconId(data.index, idx)"
							:href="ch.url||null"
							target="_blank"
							:class="{ 'channel-icon-inactive': ch.status != 'enabled', 'channel-icon-live': ch.live }"
							class="channel-icon">

							<b-avatar :src="ch.service_icon_url||null" size="1.5em" variant="dark" square></b-avatar>
						</a>
						<b-tooltip :key="`tip-${ch.service_id}-${ch.channel_token}`" :target="channelIconId(data.index, idx)" triggers="hover" placement="top">
							<div class="channel-tooltip">
								<b-avatar :src="ch.avatar_url||ch.service_icon_url||null" variant="dark" size="2em" square class="mr-2"></b-avatar>
								<span>
									<span class="d-block">{{ch.title||'(untitled)'}}</span>
									<code>{{ch.channel_token}}</code>
								</span>
							</div>
						</b-tooltip>
					</template>
				</div>
				<a href="#" class="d-block small" v-if="data.item.channels.length > 10" @click.prevent="toggleExpanded(data.item.id)">
					{{expandedUsers[data.item.id] ? 'Show less' : `Show ${data.item.channels.length - 10} more`}}
				</a>
				<code v-if="data.item.last_websocket_connect"><time-ago :date="data.item.last_websocket_connect" /></code>
				<code v-else class="text-muted">Never</code>
			</template>
			<template #cell(billing)="data">
				{{data.item.subscription_status}} {{data.item.plan}} {{data.item.period}}<br />
				<router-link v-if="data.item.restreaming_service_id" :to="{ name: 'Service', params: { id: data.item.restreaming_service_id } }">{{data.item.restreaming_service_name}}</router-link>
			</template>
		</b-table>
		<pager :per-page="perPage" :skip="skip" :total="total" @change="setPage"></pager>
		<b-alert :show="!!error" variant="danger">{{error&&(error.error||error.toString())}}</b-alert>
		<user-edit-modal ref="editModal" @saved="loadUsers"></user-edit-modal>
		<user-delete-modal ref="deleteModal" @deleted="loadUsers"></user-delete-modal>
	</div>
</template>

<script>
import Pager from '@/components/Pager.vue';
import SearchInput from '@/components/SearchInput.vue';
import UserEditModal from '@/components/User/Modal/Edit.vue';
import UserDeleteModal from '@/components/User/Modal/Delete.vue';
import TimeAgo from '@/components/TimeAgo.vue';

export default {
	name: 'Users',
	components: {
		Pager,
		SearchInput,
		UserEditModal,
		UserDeleteModal,
		TimeAgo,
	},
	data() {
		// Read from the route here (not in created()) so the initial values don't
		// trip the q/status/wsActive watchers below, which would otherwise fire on
		// mount and immediately replace the URL we just read from.
		const query = this.$route.query;
		return {
			users: [],
			error: null,
			loading: 0,
			total: 0,
			skip: parseInt(query.skip||0)||0,
			perPage: parseInt(query.perPage||10)||10,
			q: query.q || undefined,
			status: query.status || '',
			wsActive: query.wsActive || '',
			expandedUsers: {},
		};
	},
	created() {
		this.loadUsers();
	},
	watch: {
		'$route': 'loadUsers',
		'q'()        { this._applyFilters(); },
		'status'()   { this._applyFilters(); },
		'wsActive'() { this._applyFilters(); },
	},
	methods: {
		_applyFilters() {
			this.skip = 0;
			this.$router.replace({ query: {
				...this.$route.query,
				q: this.q || undefined,
				status: this.status || undefined,
				wsActive: this.wsActive || undefined,
				skip: undefined,
			} });
		},
		setPage($event) {
			this.skip = $event.skip;
			this.perPage = $event.perPage;
			this.$router.push({ query: { ...this.$route.query, skip: $event.skip || undefined, perPage: $event.perPage == 10 ? undefined : $event.perPage }});
		},
		loadUsers() {
			this.loading++;
			this.error = null;
			this.$api.get('users', { q: this.q||'', status: this.status, wsActive: this.wsActive, limit: this.perPage, skip: this.skip }).then(users => {
				this.users = users.results;
				this.total = users.total;
				const enabledChannels = users.results.flatMap(u => u.channels.filter(ch => ch.status == 'enabled'));
				Promise.allSettled(enabledChannels.map(ch => this.$api.get(`channel/${encodeURIComponent(ch.service_id)}/${encodeURIComponent(ch.channel_token)}/info`))).then(settled => {
					for(let i = 0; i < settled.length; i++)
						if(settled[i].status == 'fulfilled') {
							this.$set(enabledChannels[i], 'url', settled[i].value.url);
							this.$set(enabledChannels[i], 'live', settled[i].value.numLive > 0);
						}
				});
			}).catch(err => this.error = err).finally(() => this.loading--);
		},
		channelIconId(rowIndex, idx) {
			return `channel-icon-${rowIndex}-${idx}`;
		},
		visibleChannels(item) {
			return this.expandedUsers[item.id] ? item.channels : item.channels.slice(0, 10);
		},
		toggleExpanded(userId) {
			this.$set(this.expandedUsers, userId, !this.expandedUsers[userId]);
		},
		async syncRestreaming(userId) {
			try {
				await this.$api.post(`syncRestreaming/${userId}`);
				this.$bvToast.toast('Synchronized restreaming', {
					title: 'Success',
					variant: 'success',
					solid: true
				});
			} catch(e) {
				this.$bvToast.toast(e.toString(), {
					title: 'Error',
					variant: 'danger',
					solid: true
				});
			}
		},
		async loginAsUser(userId) {
			const { token } = await this.$api.post(`auth/${encodeURIComponent(userId)}`);
			this.$auth.pushToken(token);
			this.$router.push('/');
		},
		async grantGratuity(userId, plan) {
			try {
				await this.$api.post(`user/${userId}/gratuity`, { plan });
				this.$bvToast.toast(`Granted ${plan} gratuity`, { title: 'Success', variant: 'success', solid: true });
				this.loadUsers();
			} catch(e) {
				this.$bvToast.toast(e.error || e.toString(), { title: 'Error', variant: 'danger', solid: true });
			}
		},
		async revokeGratuity(userId) {
			try {
				await this.$api.post(`user/${userId}/gratuity`, {});
				this.$bvToast.toast('Gratuity revoked', { title: 'Success', variant: 'success', solid: true });
				this.loadUsers();
			} catch(e) {
				this.$bvToast.toast(e.error || e.toString(), { title: 'Error', variant: 'danger', solid: true });
			}
		},
	},
};
</script>

<style scoped>
.channel-icons {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	margin-bottom: 2px;
}

.channel-icon-inactive {
	opacity: .35;
}

.channel-icon-live {
	border-radius: 4px;
	box-shadow: 0 0 0 2px rgba(40, 167, 69, .9), 0 0 8px 3px rgba(40, 167, 69, .65);
}

a.channel-icon:not([href]) {
	cursor: default;
}

.channel-tooltip {
	display: flex;
	align-items: center;
	text-align: left;
}
</style>
